import { Attributes } from "html-react-parser/lib/attributes-to-props"
import React, { useContext, createContext, useEffect, useRef } from "react"
import classNames from "../utils/classnames"
import ChevronIcon from "../icons/chevron"

interface IBlockGallerySliderProps {
  children: React.ReactNode
}
interface IBlockGallerySliderWithAttributes {
  children: React.ReactNode
  attribs: Attributes
}

interface ISliderContext {
  slider: string
  items: Array<string>
  activeItem: number
  setActiveItem: (index: number) => void
  addItem: (id: string) => void
  getItemIndex: (id: string) => number
}

interface ISliderContextProviderProps {
  children: React.ReactNode
}

const SliderContext = createContext<ISliderContext>({
  slider: ``,
  items: [],
  activeItem: 0,
  setActiveItem: () => { },
  addItem: () => { },
  getItemIndex: () => 0,
})
const SliderContextProvider = ({
  children,
}: ISliderContextProviderProps): JSX.Element => {
  // random slider id
  const slider = useRef(Math.random().toString(36).substring(2, 15)).current
  const [activeItem, setActiveStateItem] = React.useState<number>(0)
  const [items, setStateItems] = React.useState<Array<string>>([])
  const getItemIndex = (id: string): number => items.indexOf(id)
  const addItem = (item: string): void => {
    setStateItems(items => [...items, item])
  }
  const setActiveItem = (index: number): void => {
    // console.log(`setActiveItem`, id)
    if (index >= 0 && index < items.length) {
      setActiveStateItem(index)
    }
  }
  return (
    <SliderContext.Provider
      value={{
        slider,
        activeItem,
        setActiveItem,
        items,
        addItem,
        getItemIndex,
      }}
    >
      {children}
    </SliderContext.Provider>
  )
}

function BlockGalleryDots(): JSX.Element {
  const { items, setActiveItem, activeItem } = useContext(SliderContext)
  return (
    <ul className="flex gap-2 w-full p-0 m-0 items-center justify-center mb-8">
      {items.map((item, index) => (
        <li
          key={index}
          onClick={(): void => setActiveItem(index)}
          className={classNames(
            index === activeItem ? `before:bg-green` : `before:bg-blue`,
            `w-4 h-4 cursor-pointer p-0 m-0`,
            `before:content-[""] before:rounded-full hover:before:bg-green before:w-4 before:h-4`
          )}
        />
      ))}
    </ul>
  )
}

function BlockGallerySlider({
  children,
}: IBlockGallerySliderProps): JSX.Element {
  // console.log(`BlockGallerySlider`, children)
  return (
    <SliderContextProvider>
      <ul className="wp-block-georgiancollege-gallery-slider p-0 m-0">
        {children}
      </ul>
      <BlockGalleryDots />
    </SliderContextProvider>
  )
}

export function BlockGallerySliderItem({
  children,
  attribs,
}: IBlockGallerySliderWithAttributes): JSX.Element {
  // console.log(`BlockGallerySliderLink`)
  const { activeItem, setActiveItem, addItem, slider, items } =
    useContext(SliderContext)
  // random id
  const id = useRef(Math.random().toString(36).substring(7)).current
  const index = items.indexOf(id)
  useEffect(() => {
    addItem(id)
  }, [id])

  // console.log({ slider, activeItem, index, items })
  return (
    <li
      className={classNames(
        `before:content-none text-center flex items-center relative m-0 p-0`,
        activeItem === index ? `block` : `hidden`
      )}
      onClick={(e): void => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <button
        className={classNames(`rotate-180 absolute left-4`)}
        type="button"
        onClick={(): void => setActiveItem(index - 1)}
        aria-label="Previous button"
      >
        <ChevronIcon
          className={classNames(
            `w-8 h-8 bg-blue hover:bg-green rounded-full p-2 text-white`,
            index !== 0 ? `opacity-1` : `opacity-0`
          )}
        />
      </button>
      <div>{children}</div>
      <button
        className={classNames(`absolute right-4`)}
        type="button"
        onClick={(): void => setActiveItem(index + 1)}
        aria-label="Next button"
      >
        <ChevronIcon
          className={classNames(
            index < items.length - 1 ? `opacity-1` : `opacity-0`,
            `w-8 h-8 bg-blue hover:bg-green rounded-full p-2 text-white`
          )}
        />
      </button>
    </li>
  )
}

export default BlockGallerySlider
